// ============================================================================
// CASE TYPE SERVICE — Phase 8a
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { caseTypeEndpoints, CASE_TYPE_BASE_PATH } from '../endpoints/caseType.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  CaseType,
  CaseTypeListItem,
  CreateCaseTypeRequest,
  UpdateCaseTypeRequest,
  CaseTypeSearchRequest,
} from '../types/caseType.types';

class CaseTypeService {
  async getAll(params?: CaseTypeSearchRequest): Promise<PaginatedResponse<CaseTypeListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(CASE_TYPE_BASE_PATH, params as Record<string, any>)
      : caseTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<CaseType>> {
    const response = await get<ApiResponse<CaseType>>(caseTypeEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<CaseType>> {
    const response = await get<ApiResponse<CaseType>>(caseTypeEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateCaseTypeRequest): Promise<ApiResponse<CaseType>> {
    const response = await post<ApiResponse<CaseType>>(caseTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateCaseTypeRequest): Promise<ApiResponse<CaseType>> {
    const response = await put<ApiResponse<CaseType>>(caseTypeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(caseTypeEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${caseTypeEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : caseTypeEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const caseTypeService = new CaseTypeService();
export default caseTypeService;
